import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Check, X, Loader2, AlertTriangle } from 'lucide-react';

export default function CalendarConnect({ api }) {
  const [status, setStatus] = useState({ connected: false, google_email: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/calendar/status');
      setStatus(data);
    } catch {
      // silent — endpoint requires CEO Life access
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    // After OAuth redirect we land back with ?calendar_connected=1 or ?calendar_error=...
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected')) {
      setFlash({ type: 'success', text: 'Google Calendar connected' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar_error')) {
      setFlash({ type: 'error', text: `Calendar connect failed: ${params.get('calendar_error')}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchStatus();
  }, [fetchStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/oauth/calendar/login');
      if (data?.authorization_url) window.location.href = data.authorization_url;
    } catch (err) {
      setFlash({ type: 'error', text: err?.response?.data?.detail || 'Failed to start Google sign-in' });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Synced events stay on Google but new pushes will stop.')) return;
    setBusy(true);
    try {
      await api.post('/calendar/disconnect');
      setFlash({ type: 'success', text: 'Disconnected from Google Calendar' });
      setStatus({ connected: false, google_email: '' });
    } catch (err) {
      setFlash({ type: 'error', text: err?.response?.data?.detail || 'Disconnect failed' });
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="ceo-pill flex items-center gap-2" style={{ padding: '6px 10px', fontSize: 11 }}>
        <Loader2 className="h-3 w-3 animate-spin" /> Calendar…
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="ceo-calendar-connect">
      {status.connected ? (
        <div className="flex items-center gap-2">
          <span
            className="ceo-pill flex items-center gap-1.5"
            style={{ padding: '6px 10px', fontSize: 11, background: 'rgba(76, 217, 100, 0.12)', color: 'var(--text)' }}
            data-testid="calendar-connected-badge"
          >
            <Check className="h-3 w-3" style={{ color: '#4CD964' }} />
            <Calendar className="h-3 w-3" />
            {status.google_email || 'Connected'}
          </span>
          <button
            onClick={disconnect}
            disabled={busy}
            className="ceo-btn ceo-btn-ghost"
            style={{ fontSize: 10, padding: '4px 8px' }}
            data-testid="calendar-disconnect-btn"
            title="Disconnect"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={connect}
          disabled={busy}
          className="ceo-btn ceo-btn-primary flex items-center gap-1.5"
          style={{ fontSize: 11, padding: '6px 12px' }}
          data-testid="calendar-connect-btn"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calendar className="h-3 w-3" />}
          Connect Google Calendar
        </button>
      )}
      {flash && (
        <span
          className="text-[10px] flex items-center gap-1"
          style={{ color: flash.type === 'success' ? '#4CD964' : '#FF6B6B' }}
        >
          {flash.type === 'error' && <AlertTriangle className="h-3 w-3" />}
          {flash.text}
        </span>
      )}
    </div>
  );
}
